import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import { MultiSelectFilterDropdown } from '@openx/components-filters/lib/FastFilters/MultiSelectFilterDropdown.tsx';
import type { Option, OptionsMap } from '@openx/types/options.ts';
import type { PackagesListFiltersValue } from '@openx/types/package.ts';
import { type JSX, memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

const CONTROL_HEIGHT = 40;
const MIN_SEARCH_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 500;

export type PackagesTopBarProps = {
  adPlacementOptions: OptionsMap;
  deviceTypeOptions: OptionsMap;
  distributionChannelOptions: OptionsMap;
  filters: PackagesListFiltersValue;
  isAdPlacementLoading: boolean;
  isDeviceTypeLoading: boolean;
  isDistributionChannelLoading: boolean;
  onChange: (filters: PackagesListFiltersValue) => void;
  organizationOptions?: Option[];
  isOrganizationLoading?: boolean;
};

export const PackagesTopBar = memo(
  ({
    adPlacementOptions,
    deviceTypeOptions,
    distributionChannelOptions,
    filters,
    isAdPlacementLoading,
    isDeviceTypeLoading,
    isDistributionChannelLoading,
    onChange,
    organizationOptions,
    isOrganizationLoading = false,
  }: PackagesTopBarProps): JSX.Element => {
    const { t } = useTranslation();
    const [phraseInput, setPhraseInput] = useState(filters.phrase ?? '');

    useEffect(() => {
      setPhraseInput(filters.phrase ?? '');
    }, [filters.phrase]);

    useEffect(() => {
      const timeoutId = window.setTimeout(() => {
        const shouldApplyPhrase = phraseInput.length >= MIN_SEARCH_LENGTH || phraseInput.length === 0;

        if (shouldApplyPhrase && phraseInput !== filters.phrase) {
          onChange({
            ...filters,
            phrase: phraseInput,
          });
        }
      }, SEARCH_DEBOUNCE_MS);

      return () => window.clearTimeout(timeoutId);
    }, [filters, onChange, phraseInput]);

    const formatOptions = useMemo(
      () =>
        Object.values(adPlacementOptions).map(({ id, name }) => ({
          label: name,
          value: id,
        })),
      [adPlacementOptions]
    );

    const channelOptions = useMemo(
      () =>
        Object.values(distributionChannelOptions).map(({ id, name }) => ({
          label: name,
          value: id,
        })),
      [distributionChannelOptions]
    );

    const deviceOptions = useMemo(
      () =>
        Object.values(deviceTypeOptions).map(({ id, name }) => ({
          label: name,
          value: id,
        })),
      [deviceTypeOptions]
    );

    const organizationFilterOptions = useMemo(
      () =>
        organizationOptions?.map(({ id, name }) => ({
          label: name,
          value: id,
        })) ?? [],
      [organizationOptions]
    );

    const selectedFormats =
      typeof filters.ad_placement === 'string'
        ? filters.ad_placement
            .split(',')
            .map(value => value.trim())
            .filter(Boolean)
        : [];
    const selectedChannels =
      typeof filters.distribution_channel === 'string'
        ? filters.distribution_channel
            .split(',')
            .map(value => value.trim())
            .filter(Boolean)
        : [];
    const selectedDevices =
      typeof filters.device_type === 'string'
        ? filters.device_type
            .split(',')
            .map(value => value.trim())
            .filter(Boolean)
        : [];
    const selectedOrganizations = filters.account_uid ?? [];
    const hasActiveFilters =
      selectedFormats.length > 0 ||
      selectedChannels.length > 0 ||
      selectedDevices.length > 0 ||
      selectedOrganizations.length > 0;

    return (
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          borderRadius: 0,
          borderLeft: 0,
          borderRight: 0,
          backgroundColor: theme => theme.palette.background.boxDark,
        }}
      >
        <Box
          data-test="packages-fast-filters-container"
          sx={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <TextField
            type="search"
            size="small"
            variant="outlined"
            placeholder={t('Search Name or ID')}
            value={phraseInput}
            onChange={event => setPhraseInput(event.target.value)}
            data-test="search-field"
            sx={{
              width: { xs: '100%', md: 320 },
              flexShrink: { xs: 0, md: 0 },
              '& .MuiInputBase-root': {
                height: CONTROL_HEIGHT,
              },
            }}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  </InputAdornment>
                ),
              },
              htmlInput: { 'data-test': 'search-field-input' },
            }}
          />

          <MultiSelectFilterDropdown
            label={t('Format')}
            options={formatOptions}
            selectedValues={selectedFormats}
            loading={isAdPlacementLoading}
            controlHeight={CONTROL_HEIGHT}
            dataTest="packages-format-filter"
            isActive={selectedFormats.length > 0}
            minWidth={0}
            onApply={values =>
              onChange({
                ...filters,
                ad_placement: values.length ? values.join(',') : undefined,
              })
            }
          />

          <MultiSelectFilterDropdown
            label={t('Channel')}
            options={channelOptions}
            selectedValues={selectedChannels}
            loading={isDistributionChannelLoading}
            controlHeight={CONTROL_HEIGHT}
            dataTest="packages-channel-filter"
            isActive={selectedChannels.length > 0}
            minWidth={0}
            onApply={values =>
              onChange({
                ...filters,
                distribution_channel: values.length ? values.join(',') : undefined,
              })
            }
          />

          <MultiSelectFilterDropdown
            label={t('Device')}
            options={deviceOptions}
            selectedValues={selectedDevices}
            loading={isDeviceTypeLoading}
            controlHeight={CONTROL_HEIGHT}
            dataTest="packages-device-filter"
            isActive={selectedDevices.length > 0}
            minWidth={0}
            onApply={values =>
              onChange({
                ...filters,
                device_type: values.length ? values.join(',') : undefined,
              })
            }
          />

          {organizationOptions !== undefined && (
            <MultiSelectFilterDropdown
              label={t('Organization')}
              options={organizationFilterOptions}
              selectedValues={selectedOrganizations}
              loading={isOrganizationLoading}
              showSearch
              searchPlaceholder={t('Search Organization')}
              controlHeight={CONTROL_HEIGHT}
              dataTest="packages-organization-filter"
              isActive={selectedOrganizations.length > 0}
              minWidth={0}
              onApply={values =>
                onChange({
                  ...filters,
                  account_uid: values.length ? values : undefined,
                })
              }
            />
          )}

          {hasActiveFilters && (
            <Button
              variant="text"
              startIcon={<CloseIcon />}
              onClick={() =>
                onChange({
                  ...filters,
                  ad_placement: undefined,
                  distribution_channel: undefined,
                  device_type: undefined,
                  account_uid: undefined,
                })
              }
              data-test="packages-clear-filters-button"
              sx={{ height: CONTROL_HEIGHT, flexShrink: 0 }}
            >
              {t('Clear Filters')}
            </Button>
          )}
        </Box>
      </Paper>
    );
  }
);
